import styled, { css, keyframes } from 'styled-components';
import { colors } from '~/styles/colors';
import { mobileQuery } from '../../ds';

const PRIMARY = colors.brand.primary.base;
const STROKE = colors.stroke['soft-200'];
const TEXT_STRONG = colors.text['main-900'];
const TEXT_SOFT = colors.text['sub-350'];

const overlayIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const modalIn = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(0.98); }
  to { opacity: 1; transform: none; }
`;

export const TabsBar = styled.div`
  display: flex;
  gap: 28px;

  ${mobileQuery} {
    flex-wrap: wrap;
    gap: 4px 12px;
  }

  margin-bottom: 20px;
  border-bottom: 1px solid ${STROKE};
`;

export const Tab = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: -1px;
  padding: 0 2px 14px;
  border: none;
  border-bottom: 3px solid ${({ $active }) => ($active ? PRIMARY : 'transparent')};
  background: none;
  font-family: inherit;
  font-size: 16px;
  font-weight: 600;
  white-space: nowrap;
  color: ${({ $active }) => ($active ? PRIMARY : TEXT_SOFT)};
  cursor: pointer;
  transition: color 0.15s;

  ${mobileQuery} {
    gap: 6px;
    font-size: 14px;
  }
`;

export const TabBadge = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  border-radius: 999px;
  background: ${({ $active }) => ($active ? colors.brand.primary.lighterAlt : colors.celsius['neutral-chip'])};
  font-size: 12px;
  font-weight: 700;
  color: ${({ $active }) => ($active ? PRIMARY : TEXT_SOFT)};
`;

export const ListHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;

  & > button {
    margin-left: auto;
  }

  ${mobileQuery} {
    & > button {
      width: 100%;
      margin-left: 0;
      padding: 15px 20px;
    }
  }
`;

export const TotalLabel = styled.span`
  font-size: 15px;
  font-weight: 700;
  white-space: nowrap;
  color: ${TEXT_STRONG};
`;

export const ListHint = styled.span`
  flex: 1;
  min-width: 200px;
  font-size: 13px;
  color: ${TEXT_SOFT};
`;

export const CardsGrid = styled.div`
  display: grid;
  /* Três cards por linha no desktop, como no design; em telas menores, dois, para os sete dias caberem. */
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 1400px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  ${mobileQuery} {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
`;

export const ScheduleCardBox = styled.div`
  min-width: 0;
  padding: 20px;
  border: 1px solid ${STROKE};
  border-left: 4px solid ${PRIMARY};
  border-radius: 10px;
  background: ${colors.bg['white-0']};

  ${mobileQuery} {
    padding: 16px;
  }
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 32px;
`;

export const CardHeaderTitle = styled.div`
  min-width: 0;
  overflow-wrap: anywhere;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: ${TEXT_STRONG};
`;

export const CardActions = styled.div`
  display: flex;
  flex: none;
  gap: 6px;
`;

export const InfoGrid = styled.div<{ $compact?: boolean }>`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${({ $compact }) => ($compact ? '8px 16px' : '10px 20px')};
  align-items: baseline;
  margin-top: ${({ $compact }) => ($compact ? '12px' : '16px')};
  padding-top: ${({ $compact }) => ($compact ? '12px' : '16px')};
  border-top: 1px solid ${STROKE};
  font-size: ${({ $compact }) => ($compact ? '13px' : '14px')};

  & > span:nth-child(odd) {
    color: ${TEXT_SOFT};
  }

  & > span:nth-child(even) {
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    text-align: right;
    color: ${TEXT_STRONG};
  }
`;

export const CardDays = styled.div`
  margin-top: 18px;
`;

export const ExceptionTable = styled.div`
  overflow: hidden;
  border: 1px solid ${STROKE};
  border-radius: 10px;
`;

const exceptionColumns = css`
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr 1fr 0.7fr 0.7fr 90px;
  gap: 12px;
  align-items: center;
  padding: 12px 18px;
`;

export const ExceptionHeaderRow = styled.div`
  ${exceptionColumns}
  border-bottom: 1px solid ${STROKE};
  background: ${colors.celsius['surface-subtle']};
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${TEXT_SOFT};
`;

export const ExceptionRow = styled.div`
  ${exceptionColumns}
  padding: 14px 18px;
  border-bottom: 1px solid ${STROKE};
  border-left: 4px solid ${PRIMARY};
  background: ${colors.bg['white-0']};
  font-size: 14px;
  color: ${colors.text['sub-500']};

  &:last-child {
    border-bottom: none;
  }

  & > span:first-child {
    font-weight: 700;
    color: ${TEXT_STRONG};
  }

  & > span:last-child {
    display: flex;
    justify-content: flex-end;
    gap: 6px;
  }
`;

export const ExceptionCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const ExceptionCardBox = styled.div`
  padding: 16px;
  border: 1px solid ${STROKE};
  border-left: 4px solid ${PRIMARY};
  border-radius: 10px;
  background: ${colors.bg['white-0']};
`;

export const EmptyState = styled.div`
  padding: 48px 20px;
  border: 1px dashed ${STROKE};
  border-radius: 10px;
  text-align: center;
  font-size: 13px;
  color: ${TEXT_SOFT};

  & > strong {
    display: block;
    margin-bottom: 4px;
    font-size: 15px;
    font-weight: 600;
    color: ${colors.text['sub-500']};
  }
`;

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  /* Acima do menu lateral e do cabeçalho da aplicação, como o ModalWindow. */
  z-index: 1000001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: ${colors.celsius.overlay};
  animation: ${overlayIn} 0.2s ease;

  ${mobileQuery} {
    align-items: flex-end;
    padding: 0;
  }
`;

export const ModalPanel = styled.dialog<{ $width: number }>`
  position: static;
  width: ${({ $width }) => $width}px;
  margin: 0;
  padding: 0;
  border: 0;
  color: inherit;
  max-width: 100%;
  max-height: 92vh;
  overflow: auto;
  border-radius: 14px;
  background: ${colors.bg['white-0']};
  box-shadow: 0 24px 60px ${colors.celsius['shadow-modal']};
  animation: ${modalIn} 0.25s ease;

  &::before {
    content: "";
    display: block;
    height: 8px;
    background: ${PRIMARY};
  }

  ${mobileQuery} {
    width: 100%;
    max-height: 88%;
    border-radius: 18px 18px 0 0;

    &::before {
      height: 6px;
    }
  }
`;

export const ModalBody = styled.form`
  padding: 28px 32px 30px;
  font-family: Inter, sans-serif;

  ${mobileQuery} {
    padding: 22px 18px 26px;
  }
`;

export const ModalTitle = styled.h3`
  margin: 0;
  font-size: 21px;
  font-weight: 700;
  color: ${TEXT_STRONG};
`;

export const ModalSubtitle = styled.p`
  margin: 6px 0 0;
  font-size: 13px;
  color: ${TEXT_SOFT};
`;

export const ModalLabel = styled.label`
  display: block;
  margin: 24px 0 8px;
  font-size: 13px;
  font-weight: 700;
  color: ${TEXT_STRONG};
`;

export const ModalDivider = styled.div`
  margin: 22px 0;
  border-top: 1px solid ${STROKE};
`;

const inputBase = css`
  width: 100%;
  border: 1px solid ${STROKE};
  border-radius: 8px;
  font-family: inherit;
  font-size: 15px;
  color: ${TEXT_STRONG};
  outline: none;

  &:focus {
    border-color: ${PRIMARY};
    box-shadow: 0 0 0 3px ${colors.brand.primary.light};
  }
`;

export const TextInput = styled.input`
  ${inputBase}
  padding: 14px 16px;
`;

export const TimeInput = styled.input`
  ${inputBase}
  padding: 12px 14px;
`;

export const TimeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 16px;
  margin-top: 22px;

  & > label {
    padding-bottom: 12px;
  }
`;

export const TimeField = styled.div`
  flex: 1;
  min-width: 140px;

  & > label {
    margin-top: 0;
  }
`;

export const InlineCheck = styled.label`
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 14px;
  font-weight: 500;
  color: ${TEXT_STRONG};
  cursor: pointer;

  & > input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }
`;

export const PermissionSegment = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  padding: 4px;
  border: 1px solid ${STROKE};
  border-radius: 10px;
  background: ${colors.bg['weak-100']};
`;

export const PermissionOption = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 0;
  padding: 11px 12px;
  border: 1.5px solid ${({ $active }) => ($active ? colors.brand.primary.dark : 'transparent')};
  border-radius: 8px;
  background: ${({ $active }) => ($active ? PRIMARY : 'transparent')};
  box-shadow: ${({ $active }) => ($active ? `inset 0 0 0 1px ${colors.bg['white-0']}` : 'none')};
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  color: ${({ $active }) => ($active ? colors.text['white-0'] : colors.text['sub-500'])};
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: ${({ $active }) => ($active ? PRIMARY : colors.brand.primary.lighter)};
    color: ${({ $active }) => ($active ? colors.text['white-0'] : PRIMARY)};
  }

  &:focus-visible {
    outline: 2px solid ${PRIMARY};
    outline-offset: 2px;
  }

  & > svg {
    flex: none;
  }
`;

export const PermissionHint = styled.p`
  margin: 8px 0 0;
  font-size: 13px;
  color: ${colors.text['sub-500']};
`;

export const SubmitButton = styled.button`
  width: 100%;
  margin-top: 28px;
  padding: 15px;
  border: none;
  border-radius: 8px;
  background: ${PRIMARY};
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${colors.text['white-0']};
  cursor: pointer;

  &:hover {
    background: ${colors.brand.principalHover};
    color: ${colors.White};
  }
`;

export const CancelLink = styled.button`
  display: block;
  margin: 14px auto 0;
  padding: 0;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  text-decoration: underline;
  color: ${PRIMARY};
  cursor: pointer;
`;

export const ModalButtons = styled.div`
  display: flex;
  gap: 14px;
  margin-top: 28px;

  & > button {
    flex: 1;
    margin-top: 0;
  }
`;

export const BackButton = styled.button`
  padding: 15px;
  border: 1.5px solid ${colors.celsius['danger-base']};
  border-radius: 8px;
  background: ${colors.bg['white-0']};
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${colors.celsius['danger-base']};
  cursor: pointer;

  &:hover {
    background: ${colors.celsius['danger-hover']};
  }
`;

export const ModalError = styled.span`
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: ${colors.celsius['danger-base']};
`;

/* Calendário do campo de data (design da Alice sobre os tokens do design system Celsius). */

/** Até esta largura o calendário vira folha na base da tela, em vez de ancorado ao campo. */
export const DATE_SHEET_BREAKPOINT = 640;
const dateSheetQuery = `@media (max-width: ${DATE_SHEET_BREAKPOINT}px)`;

export const DateTrigger = styled.button<{ $open: boolean; $invalid: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 48px;
  padding: 0 16px;
  border: 1px solid ${({ $open, $invalid }) => {
    if ($invalid) return colors.celsius['danger-base'];
    return $open ? PRIMARY : STROKE;
  }};
  border-radius: 8px;
  background: ${colors.bg['white-0']};
  box-shadow: ${({ $open }) => ($open ? `0 0 0 3px ${colors.brand.primary.light}` : 'none')};
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    border-color: ${PRIMARY};
  }

  &:focus-visible {
    border-color: ${PRIMARY};
    outline: none;
    box-shadow: 0 0 0 3px ${colors.brand.primary.light};
  }

  & > svg:first-child {
    flex: none;
    color: ${PRIMARY};
  }

  & > svg:last-child {
    flex: none;
    color: ${colors.text['sub-500']};
    transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'none')};
    transition: transform 0.18s ease;
  }
`;

export const DateTriggerValue = styled.span<{ $placeholder: boolean }>`
  flex: 1;
  min-width: 0;
  font-size: 15px;
  font-weight: ${({ $placeholder }) => ($placeholder ? 400 : 600)};
  font-variant-numeric: tabular-nums;
  color: ${({ $placeholder }) => ($placeholder ? TEXT_SOFT : TEXT_STRONG)};
`;

export const DatePopover = styled.div`
  position: fixed;
  /* Acima do Overlay dos modais. */
  z-index: 1000002;
  padding: 16px 16px 12px;
  border: 1px solid ${STROKE};
  border-radius: 10px;
  background: ${colors.bg['white-0']};
  box-shadow: 0 12px 28px ${colors.celsius['shadow-popover']};
  animation: ${overlayIn} 0.15s ease;

  /* Celular: folha presa à base da tela, com rolagem interna. */
  ${dateSheetQuery} {
    right: 8px;
    bottom: 8px;
    left: 8px;
    width: auto;
    max-height: calc(100dvh - 16px);
    overflow-y: auto;
  }
`;

export const CalendarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
`;

export const CalendarTitle = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: ${TEXT_STRONG};
`;

export const CalendarNavButton = styled.button`
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  padding: 0;
  border: 1px solid ${STROKE};
  border-radius: 8px;
  background: ${colors.bg['white-0']};
  color: ${TEXT_STRONG};
  cursor: pointer;
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;

  &:hover {
    border-color: ${PRIMARY};
    background: ${colors.brand.primary.lighter};
    color: ${PRIMARY};
  }

  &:focus-visible {
    outline: 2px solid ${PRIMARY};
    outline-offset: 1px;
  }
`;

export const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
`;

export const CalendarWeekDay = styled.span`
  padding: 6px 0;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  color: ${colors.text['sub-500']};
`;

export const CalendarDay = styled.button<{ $active: boolean; $today: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 42px;
  padding: 0;
  border: 1px solid ${({ $active, $today }) => ($today && !$active ? PRIMARY : 'transparent')};
  border-radius: 8px;
  background: ${({ $active }) => ($active ? PRIMARY : 'transparent')};
  font-family: inherit;
  font-size: 15px;
  font-weight: ${({ $active, $today }) => ($active || $today ? 600 : 400)};
  font-variant-numeric: tabular-nums;
  color: ${({ $active, $today }) => {
    if ($active) return colors.text['white-0'];
    return $today ? PRIMARY : TEXT_STRONG;
  }};
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:not(:disabled):hover {
    background: ${({ $active }) => ($active ? PRIMARY : colors.brand.primary.lighter)};
    color: ${({ $active }) => ($active ? colors.text['white-0'] : PRIMARY)};
  }

  &:disabled {
    color: ${colors.text['sub-300']};
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${PRIMARY};
    outline-offset: 1px;
  }
`;

/** Dias dos meses vizinhos, só para completar as semanas. */
export const CalendarOutsideDay = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 42px;
  font-size: 15px;
  font-variant-numeric: tabular-nums;
  color: ${colors.text['sub-300']};
`;

export const CalendarFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  padding-top: 12px;
  border-top: 1px solid ${STROKE};
`;

export const CalendarFooterButton = styled.button<{ $primary?: boolean }>`
  padding: 6px 4px;
  border: none;
  border-radius: 4px;
  background: none;
  font-family: inherit;
  font-size: 14px;
  font-weight: ${({ $primary }) => ($primary ? 700 : 600)};
  color: ${({ $primary }) => ($primary ? PRIMARY : colors.text['sub-500'])};
  cursor: pointer;

  &:hover {
    color: ${({ $primary }) => ($primary ? colors.brand.primary.dark : TEXT_STRONG)};
    text-decoration: underline;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    text-decoration: none;
  }

  &:focus-visible {
    outline: 2px solid ${PRIMARY};
    outline-offset: 1px;
  }
`;
